import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import useQueryGetMe from '@/api/auth/getMe.ts';
import { DeviceStatus } from '@/api/user/getUsers.ts';
import clustering from 'density-clustering';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { IReqGetUsersBbox, IUserBbox } from '@/api/user/getUsersBbox.ts';
import UnifiedMap from '@/component/map/unified/UnifiedMap';
import UnifiedMarker from '@/component/map/unified/UnifiedMarker';
import UnifiedCircle from '@/component/map/unified/UnifiedCircle';
import UnifiedInfoWindow from '@/component/map/unified/UnifiedInfoWindow';

import { useMapType } from '@/component/map/MapContext';
import { reverseGeocode } from '@/component/map/unified/UnifiedGeocoder';
import { bd09ToWgs84, wgs84ToBd09 } from '@/util/coordTransform';

interface IProps {
    className?: string;
    users?: IUserBbox[];
    onBoundsChange?: (bounds: IReqGetUsersBbox) => void;
}

const CACHE__ZOOM_LEVEL = 'dashboard_map__zoom';
const CACHE__CENTER_X = 'dashboard_map__center__longitude';
const CACHE__CENTER_Y = 'dashboard_map__center__latitude';

const DashboardMapForCareManager = forwardRef(({ className, users, onBoundsChange }: IProps, ref: ForwardedRef<any>) => {
    const [map, setMap] = useState<any>(null);
    const [userInfo, setUserInfo] = useState<IMarkerInfo>();
    const [zoomLevel, setZoomLevel] = useState(0);
    const { data: me } = useQueryGetMe();
    const { mapType } = useMapType();

    const getBaiduBoundsAsWgs84 = () => {
        if (!map) return null;
        const mapBounds = map.getBounds();
        if (!mapBounds) return null;
        const sw = mapBounds.getSouthWest();
        const ne = mapBounds.getNorthEast();
        const [swLat, swLng] = bd09ToWgs84(sw.lat, sw.lng);
        const [neLat, neLng] = bd09ToWgs84(ne.lat, ne.lng);
        return { startLat: swLat, endLat: neLat, startLng: swLng, endLng: neLng };
    };

    // Baidu 맵 초기화 후 bounds를 즉시 전달
    useEffect(() => {
        if (!map || mapType !== 'baidu') return;
        const timer = setTimeout(() => {
            const bounds = getBaiduBoundsAsWgs84();
            if (bounds && onBoundsChange) {
                onBoundsChange(bounds);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [map, mapType]);

    useImperativeHandle(ref, () => ({
        setLocation(latitude: number, longitude: number, zoom?: number) {
            if (
                typeof latitude === 'number' &&
                typeof longitude === 'number' &&
                !isNaN(latitude) &&
                !isNaN(longitude) &&
                isFinite(latitude) &&
                isFinite(longitude)
            ) {
                if (mapType === 'google' && map) {
                    map.panTo({ lat: latitude, lng: longitude });
                    if (zoom && typeof zoom === 'number' && !isNaN(zoom) && isFinite(zoom)) {
                        map.setZoom(zoom);
                    }
                } else if (mapType === 'baidu' && map) {
                    const [bdLat, bdLng] = wgs84ToBd09(latitude, longitude);
                    const point = new window.BMapGL.Point(bdLng, bdLat);
                    map.panTo(point);
                    if (zoom) map.setZoom(zoom);
                }
            } else {
                console.warn('Invalid coordinates provided to setLocation:', { latitude, longitude, zoom });
            }
        }
    }));

    const handleIdle = () => {
        if (!map) return;

        if (mapType === 'google') {
            const mapBounds = map.getBounds();
            if (mapBounds && onBoundsChange) {
                const ne = mapBounds.getNorthEast();
                const sw = mapBounds.getSouthWest();
                onBoundsChange({
                    startLat: sw.lat(),
                    endLat: ne.lat(),
                    startLng: sw.lng(),
                    endLng: ne.lng()
                });
            }
        } else if (mapType === 'baidu') {
            const bounds = getBaiduBoundsAsWgs84();
            if (bounds && onBoundsChange) {
                onBoundsChange(bounds);
            }
        }
    };

    const openUserInfo = async (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};
            const address = await reverseGeocode(mapType, latitude, longitude);
            const infoWithAddress: IMarkerInfo = {
                ...info!,
                address
            };
            setUserInfo(infoWithAddress);
        }
    };


    const markers: IMarker[] = useMemo(() => {
        const filtered = users?.filter(item => item?.deviceStatus) || [];
        const radius = new Map<number, number>();
        radius.set(21, 0.000005);
        radius.set(20, 0.00001);
        radius.set(19, 0.00002);
        radius.set(18, 0.00005);
        radius.set(17, 0.0001);
        radius.set(16, 0.0002);
        radius.set(15, 0.0005);
        radius.set(14, 0.001);
        radius.set(13, 0.002);
        radius.set(12, 0.005);
        radius.set(11, 0.01);
        radius.set(10, 0.02);
        radius.set(9, 0.05);
        radius.set(8, 0.1);
        radius.set(7, 0.2);
        radius.set(6, 0.5);
        radius.set(5, 1);
        radius.set(4, 2);
        radius.set(3, 5);
        radius.set(2, 10);
        radius.set(1, 20);

        const dbscan = new clustering.DBSCAN();
        const clusters = dbscan.run(
            filtered?.map(item => [item?.location?.longitude || 0, item?.location?.latitude || 0]) || [],
            radius.get(zoomLevel) ?? 0.000005,
            2
        );

        const markers: IMarker[] = [];

        clusters?.map(ids => {
            const item = filtered[ids[0]];
            const filteredUsers = ids?.map(id => filtered[id]);

            let deviceStatus;
            if (filteredUsers?.find(item => item?.deviceStatus === DeviceStatus.EMERGENCY)) {
                deviceStatus = DeviceStatus.EMERGENCY;
            } else if (filteredUsers?.find(item => item?.deviceStatus === DeviceStatus.ONLINE)) {
                deviceStatus = DeviceStatus.ONLINE;
            } else if (filteredUsers?.find(item => item?.deviceStatus === DeviceStatus.CHARGING)) {
                deviceStatus = DeviceStatus.CHARGING;
            } else {
                deviceStatus = DeviceStatus.OFFLINE;
            }

            markers?.push({
                id: item?.id,
                deviceStatus: deviceStatus,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.CLUSTER,
                users: ids?.map(id => filtered[id])
            });
        });

        dbscan?.noise?.map(id => {
            const item = filtered[id];
            markers?.push({
                id: item?.id,
                deviceStatus: item?.deviceStatus,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.SINGLE,
                users: [item]
            });
        });

        return markers;
    }, [users, zoomLevel]);

    return (
        <div className={`${className} relative`}>
            <div style={{ width: '100%', height: '100%' }}>
                {me && (
                    <UnifiedMap
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        center={{
                            lat: Number(localStorage.getItem(CACHE__CENTER_Y)) || me?.latitude || 37.5759,
                            lng: Number(localStorage.getItem(CACHE__CENTER_X)) || me?.longitude || 126.9768
                        }}
                        zoom={Number(localStorage.getItem(CACHE__ZOOM_LEVEL)) || 18}
                        onZoomChanged={() => {
                            if (map) {
                                setZoomLevel(map.getZoom() || 18);
                                localStorage.setItem(CACHE__ZOOM_LEVEL, map.getZoom()?.toString() || '18');
                            }
                        }}
                        onCenterChanged={() => {
                            if (mapType === 'google' && map) {
                                const center = map.getCenter();
                                if (center) {
                                    localStorage.setItem(CACHE__CENTER_X, center.lng().toString());
                                    localStorage.setItem(CACHE__CENTER_Y, center.lat().toString());
                                }
                            } else if (mapType === 'baidu' && map) {
                                const center = map.getCenter();
                                if (center) {
                                    const [wgsLat, wgsLng] = bd09ToWgs84(center.lat, center.lng);
                                    localStorage.setItem(CACHE__CENTER_X, wgsLng.toString());
                                    localStorage.setItem(CACHE__CENTER_Y, wgsLat.toString());
                                }
                            }
                        }}
                        onIdle={handleIdle}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                    >
                        {markers?.map(item => (
                            <UnifiedMarker
                                key={`marker_${item?.id}`}
                                position={{ lat: item?.location?.latitude || 0, lng: item?.location?.longitude || 0 }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                        (() => {
                                            let color: string = '#2966F4';
                                            switch (item?.deviceStatus) {
                                                case DeviceStatus.EMERGENCY:
                                                    color = '#FF003D';
                                                    break;
                                                case DeviceStatus.ONLINE:
                                                    color = '#2966F4';
                                                    break;
                                                case DeviceStatus.CHARGING:
                                                    color = '#ADFF45';
                                                    break;
                                                default:
                                                    color = '#BABABA';
                                                    break;
                                            }

                                            return `
                                                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                    <circle cx="25" cy="25" r="20" fill="${color}" stroke="white" stroke-width="3"/>
                                                    <text x="25" y="30" fill="white" font-size="20" font-family="Arial" text-anchor="middle">
                                                        ${item?.users?.length}
                                                    </text>
                                                </svg>
                                            `;
                                        })() as string
                                    )}`,
                                    scaledSize: { width: 50, height: 50 },
                                }}
                                onClick={() => openUserInfo(item)}
                            />
                        ))}

                        {userInfo?.type === MarketTYPE.SINGLE && (
                            <UnifiedCircle
                                center={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                radius={userInfo?.location?.accuracy || 0}
                                options={{
                                    fillColor: '#78bfffcc',
                                    strokeColor: '#78bfffcc',
                                    strokeWeight: 1,
                                }}
                            />
                        )}
                        {userInfo && (
                            <UnifiedInfoWindow
                                position={{ lat: userInfo.location?.latitude || 0, lng: userInfo.location?.longitude || 0 }}
                                onCloseClick={() => setUserInfo(undefined)}
                            >
                                <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                    <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                </div>
                            </UnifiedInfoWindow>
                        )}
                    </UnifiedMap>
                )}
            </div>
        </div>
    );
});

export default DashboardMapForCareManager;
